import { useMemo, useState } from 'react';
import type { CronRunHandlerOutcome, CronRunRecord } from '@nadoutong/contracts';
import { useCronRuns, type CronRunFilters } from '../api/cronRuns.ts';
import { useT } from '../i18n/index.ts';

/**
 * `/cron-runs` — control-loops tick history. Epic #109 P3.17.
 *
 * Table of recent ticks with filter chips for handler name + since
 * timestamp. Each row renders its per-handler outcome summary; a
 * `<details>` block exposes the raw outcome JSON.
 */

function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function CronRunsPage(): JSX.Element {
  const t = useT();
  const [handler, setHandler] = useState('');
  const [since, setSince] = useState('');

  const filters = useMemo<CronRunFilters>(() => {
    return {
      ...(handler.length > 0 ? { handler } : {}),
      ...(since.length > 0 ? { since } : {}),
    };
  }, [handler, since]);

  const query = useCronRuns(filters);

  return (
    <section className="flex flex-col gap-8" data-testid="cron-runs-page">
      <header className="flex flex-col gap-3">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('cron_runs.title')}
        </h2>
        <p className="text-sm text-on-surface-variant">{t('cron_runs.subtitle')}</p>
      </header>

      <article
        data-testid="cron-runs-filters-card"
        className="flex flex-wrap items-center gap-4 rounded-xl bg-surface-container p-6"
      >
        <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.08em] text-outline">
          {t('cron_runs.filter.handler')}
          <input
            type="text"
            value={handler}
            onChange={(e) => setHandler(e.currentTarget.value)}
            data-testid="cron-runs-filter-handler"
            placeholder={t('cron_runs.filter.handler_placeholder')}
            className="min-w-[220px] rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-1.5 text-sm text-on-surface focus:border-primary focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.08em] text-outline">
          {t('cron_runs.filter.since')}
          <input
            type="text"
            value={since}
            onChange={(e) => setSince(e.currentTarget.value)}
            data-testid="cron-runs-filter-since"
            placeholder={t('cron_runs.filter.since_placeholder')}
            className="min-w-[220px] rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-1.5 text-sm text-on-surface focus:border-primary focus:outline-none"
          />
        </label>
      </article>

      <article
        data-testid="cron-runs-list-card"
        className="flex flex-col gap-4 rounded-xl bg-surface-container p-6"
      >
        {query.isLoading ? (
          <p className="text-xs text-outline" data-testid="cron-runs-loading">
            {t('cron_runs.loading')}
          </p>
        ) : query.isError ? (
          <p
            className="text-xs text-error"
            role="alert"
            data-testid="cron-runs-error"
          >
            {t('cron_runs.error')}
          </p>
        ) : (query.data ?? []).length === 0 ? (
          <p className="text-xs text-outline" data-testid="cron-runs-empty">
            {t('cron_runs.empty')}
          </p>
        ) : (
          <RunsTable runs={query.data ?? []} />
        )}
      </article>
    </section>
  );
}

interface RunsTableProps {
  readonly runs: readonly CronRunRecord[];
}

function RunsTable({ runs }: RunsTableProps): JSX.Element {
  const t = useT();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs" data-testid="cron-runs-table">
        <thead>
          <tr className="border-b border-outline-variant text-[10px] uppercase tracking-[0.08em] text-outline">
            <th scope="col" className="py-2 pr-4 font-semibold">
              {t('cron_runs.column.scheduled_at')}
            </th>
            <th scope="col" className="py-2 pr-4 text-right font-semibold">
              {t('cron_runs.column.duration')}
            </th>
            <th scope="col" className="py-2 pr-4 text-right font-semibold">
              {t('cron_runs.column.due')}
            </th>
            <th scope="col" className="py-2 pr-4 text-right font-semibold">
              {t('cron_runs.column.ok')}
            </th>
            <th scope="col" className="py-2 pr-4 text-right font-semibold">
              {t('cron_runs.column.fail')}
            </th>
            <th scope="col" className="py-2 pr-4 font-semibold">
              {t('cron_runs.column.outcomes')}
            </th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <RunRow key={run.id} run={run} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface RunRowProps {
  readonly run: CronRunRecord;
}

function RunRow({ run }: RunRowProps): JSX.Element {
  const t = useT();
  const hasFail = run.fail_count > 0;
  return (
    <tr
      className="border-b border-outline-variant/40"
      data-testid={`cron-runs-row-${run.id}`}
      data-has-fail={hasFail ? 'true' : 'false'}
    >
      <td className="py-2 pr-4 font-mono text-[11px] text-outline">{run.scheduled_at}</td>
      <td className="py-2 pr-4 text-right font-mono text-[11px] text-on-surface">
        {fmtMs(run.duration_ms)}
      </td>
      <td className="py-2 pr-4 text-right font-mono text-[11px] text-on-surface">
        {run.due_count}
      </td>
      <td className="py-2 pr-4 text-right font-mono text-[11px] text-on-surface">
        {run.ok_count}
      </td>
      <td
        className={`py-2 pr-4 text-right font-mono text-[11px] ${
          hasFail ? 'font-semibold text-error' : 'text-on-surface'
        }`}
      >
        {run.fail_count}
      </td>
      <td className="py-2 pr-4">
        <div className="flex flex-wrap gap-1">
          {run.outcomes.map((o) => (
            <OutcomeChip key={o.name} outcome={o} />
          ))}
        </div>
        {run.outcomes.length > 0 ? (
          <details className="mt-2">
            <summary className="cursor-pointer text-[11px] text-outline">
              {t('cron_runs.outcomes.expand')}
            </summary>
            <pre
              className="mt-2 max-h-60 overflow-y-auto whitespace-pre-wrap rounded bg-surface-container-lowest p-2 font-mono text-[10px] text-on-surface"
              data-testid={`cron-runs-outcomes-${run.id}`}
            >
              {JSON.stringify(run.outcomes, null, 2)}
            </pre>
          </details>
        ) : null}
      </td>
    </tr>
  );
}

interface OutcomeChipProps {
  readonly outcome: CronRunHandlerOutcome;
}

function OutcomeChip({ outcome }: OutcomeChipProps): JSX.Element {
  const cls = outcome.ok
    ? 'inline-flex items-center gap-1 rounded-full bg-surface-container-highest px-2 py-0.5 text-[10px] font-bold uppercase text-on-surface'
    : 'inline-flex items-center gap-1 rounded-full bg-tertiary-container px-2 py-0.5 text-[10px] font-bold uppercase text-on-tertiary-container';
  return (
    <span className={cls} data-testid={`cron-runs-outcome-chip-${outcome.name}`}>
      {outcome.name}
      {outcome.ok ? '' : ' · fail'}
    </span>
  );
}
